import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useCart, useWishlist } from '../../context';
import { ROUTES } from '../../config/constants';
import LoginModal from './LoginModal';
import CartDrawer from './CartDrawer';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState<boolean>(false);
  const [isCartOpen, setIsCartOpen] = useState<boolean>(false);
  const { user, backendUser, logout } = useAuth();
  const { getTotalItems } = useCart();
  const { getItemCount } = useWishlist();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 bg-canvas-inverted-secondary border-b border-stroke-inverted-secondary">
      <div className="max-w-container mx-auto px-6 md:px-12 py-4">
        <div className="flex justify-between items-center">
          <a href="/" className="flex items-center hover:opacity-80 transition-opacity cursor-pointer">
            <img
              src="/Logo TOPPED blanco-01.png"
              alt="TOPPED"
              className="h-12 md:h-14 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex gap-8">
            <a href="/#shop" className="text-sm font-medium uppercase tracking-wider text-fg-inverted-tertiary hover:text-fg-inverted-primary transition-colors duration-300">
              Tienda
            </a>
            <a href="/#about" className="text-sm font-medium uppercase tracking-wider text-fg-inverted-tertiary hover:text-fg-inverted-primary transition-colors duration-300">
              Nosotros
            </a>
            <a href="/#contact" className="text-sm font-medium uppercase tracking-wider text-fg-inverted-tertiary hover:text-fg-inverted-primary transition-colors duration-300">
              Contacto
            </a>
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex gap-4 items-center">
            {/* Wishlist Button */}
            <button
              onClick={() => navigate('/wishlist')}
              className="relative group p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-canvas-inverted-tertiary/10 rounded-lg transition-colors duration-300"
              aria-label="Wishlist"
            >
              <svg className="w-6 h-6 text-fg-inverted-tertiary group-hover:text-fg-inverted-primary transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              {getItemCount() > 0 && (
                <span className="absolute -top-1 -right-1 bg-pink-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                  {getItemCount()}
                </span>
              )}
            </button>

            {/* Cart Button */}
            <button
              onClick={() => setIsCartOpen(true)}
              className="relative group p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-canvas-inverted-tertiary/10 rounded-lg transition-colors duration-300"
              aria-label="Open cart"
            >
              <svg className="w-6 h-6 text-fg-inverted-tertiary group-hover:text-fg-inverted-primary transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              {getTotalItems() > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                  {getTotalItems()}
                </span>
              )}
            </button>
            {user ? (
              <div className="flex items-center gap-4">
                {backendUser?.role === 'admin' && (
                  <button
                    onClick={() => navigate('/admin')}
                    className="px-3 py-1.5 text-xs font-medium uppercase tracking-wide bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Panel Admin
                  </button>
                )}
                <button
                  onClick={() => navigate(ROUTES.profile)}
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                >
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt={user.displayName || 'Usuario'}
                      className="w-8 h-8 rounded-full object-cover"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-fg-inverted-tertiary flex items-center justify-center text-xs font-bold text-canvas-inverted-secondary">
                      {user.displayName?.charAt(0).toUpperCase() || 'U'}
                    </div>
                  )}
                  <span className="text-sm text-fg-inverted-primary">
                    {user.displayName}
                  </span>
                </button>
                <button
                  onClick={logout}
                  className="text-sm font-medium uppercase tracking-wide text-fg-inverted-tertiary hover:text-fg-inverted-primary transition-colors duration-300"
                >
                  Salir
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsLoginModalOpen(true)}
                className="text-sm font-medium uppercase tracking-wide text-fg-inverted-tertiary hover:text-fg-inverted-primary transition-colors duration-300"
              >
                Ingresar
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-fg-inverted-primary p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-canvas-inverted-tertiary/10 rounded-lg transition-colors duration-300"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-stroke-inverted-secondary pt-4">
            <nav className="flex flex-col gap-2">
              <a href="/#shop" className="text-sm font-medium uppercase tracking-wider text-fg-inverted-tertiary hover:text-fg-inverted-primary transition-colors duration-300 py-3 px-4 rounded-lg hover:bg-canvas-inverted-tertiary/10 min-h-[44px] flex items-center">
                Tienda
              </a>
              <a href="/#about" className="text-sm font-medium uppercase tracking-wider text-fg-inverted-tertiary hover:text-fg-inverted-primary transition-colors duration-300 py-3 px-4 rounded-lg hover:bg-canvas-inverted-tertiary/10 min-h-[44px] flex items-center">
                Nosotros
              </a>
              <a href="/#contact" className="text-sm font-medium uppercase tracking-wider text-fg-inverted-tertiary hover:text-fg-inverted-primary transition-colors duration-300 py-3 px-4 rounded-lg hover:bg-canvas-inverted-tertiary/10 min-h-[44px] flex items-center">
                Contacto
              </a>
              <button
                onClick={() => {
                  navigate('/wishlist');
                  setIsMenuOpen(false);
                }}
                className="flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-fg-inverted-tertiary hover:text-fg-inverted-primary transition-colors duration-300 text-left py-3 px-4 rounded-lg hover:bg-canvas-inverted-tertiary/10 min-h-[44px]"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                Lista de Deseos ({getItemCount()})
              </button>
              <button
                onClick={() => {
                  setIsCartOpen(true);
                  setIsMenuOpen(false);
                }}
                className="flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-fg-inverted-tertiary hover:text-fg-inverted-primary transition-colors duration-300 text-left py-3 px-4 rounded-lg hover:bg-canvas-inverted-tertiary/10 min-h-[44px]"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                Carrito ({getTotalItems()})
              </button>
              {user ? (
                <>
                  {backendUser?.role === 'admin' && (
                    <button
                      onClick={() => {
                        navigate('/admin');
                        setIsMenuOpen(false);
                      }}
                      className="px-4 py-3 text-sm font-medium uppercase tracking-wide bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors min-h-[44px]"
                    >
                      Panel Admin
                    </button>
                  )}
                  <button
                    onClick={() => {
                      navigate(ROUTES.profile);
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center gap-2 py-3 px-4 hover:bg-canvas-inverted-tertiary/10 transition-colors rounded-lg min-h-[44px]"
                  >
                    {user.photoURL ? (
                      <img
                        src={user.photoURL}
                        alt={user.displayName || 'Usuario'}
                        className="w-8 h-8 rounded-full object-cover"
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-fg-inverted-tertiary flex items-center justify-center text-xs font-bold text-canvas-inverted-secondary">
                        {user.displayName?.charAt(0).toUpperCase() || 'U'}
                      </div>
                    )}
                    <span className="text-sm text-fg-inverted-primary">
                      {user.displayName}
                    </span>
                  </button>
                  <button
                    onClick={logout}
                    className="text-sm font-medium uppercase tracking-wide text-fg-inverted-tertiary hover:text-fg-inverted-primary transition-colors duration-300 text-left py-3 px-4 rounded-lg hover:bg-canvas-inverted-tertiary/10 min-h-[44px]"
                  >
                    Salir
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsLoginModalOpen(true)}
                  className="text-sm font-medium uppercase tracking-wide text-fg-inverted-tertiary hover:text-fg-inverted-primary transition-colors duration-300 text-left py-3 px-4 rounded-lg hover:bg-canvas-inverted-tertiary/10 min-h-[44px]"
                >
                  Ingresar
                </button>
              )}
            </nav>
          </div>
        )}
      </div>

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
      />

      <CartDrawer
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
      />
    </header>
  );
};

export default Header;
